import { Receipt, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import styled from 'styled-components';
import { Card } from '../components/common/Card';
import Footer from '../components/layout/Footer';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import { useMenu } from '../context/MenuContext';
import ordersService from '../services/ordersService';
import { media, theme } from '../styles/theme';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.background};
  animation: fadeIn 0.5s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  padding-bottom: 100px;
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    padding-bottom: 80px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: ${theme.spacing.sm};
    padding-bottom: 80px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing['2xl']};
  
  h1 {
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing.sm};
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accentTeal} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.lg};
    margin: 0;
  }

  ${media.down.sm} {
    margin-bottom: ${theme.spacing.xl};
    
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
    
    p {
      font-size: ${theme.typography.fontSize.base};
    }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const SummaryCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border: none;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  position: relative;

  svg { opacity: 0.95; }

  ${media.down.sm} {
    padding: ${theme.spacing.sm};
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color }) => `${color || theme.colors.primary}10`};
  color: ${({ color }) => color || theme.colors.primary};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }

  ${SummaryCard}:hover & {
    background: ${({ color }) => `${color || theme.colors.primary}15`};
  }

  ${media.down.sm} {
    width: 48px;
    height: 48px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const SummaryContent = styled.div`
  flex: 1;
  
  h3 {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.xs};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  ${media.down.sm} {
    h3 {
      font-size: ${theme.typography.fontSize.xs};
    }
    
    p {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const RupeesIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: inherit;
  font-family: 'Arial', sans-serif;
`;

const ChartCard = styled(Card)`
  padding: ${theme.spacing.xl};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.sm};
  
  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
    padding-bottom: ${theme.spacing.md};
    border-bottom: 1px solid ${theme.colors.border};
  }

  &:hover {
    box-shadow: ${theme.shadows.md};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.lg};
    
    h2 {
      font-size: ${theme.typography.fontSize.lg};
    }
  }
`;

// Mock data
const salesData = [
  { name: 'Mon', sales: 12000 },
  { name: 'Tue', sales: 15000 },
  { name: 'Wed', sales: 18000 },
  { name: 'Thu', sales: 14000 },
  { name: 'Fri', sales: 22000 },
  { name: 'Sat', sales: 25000 },
  { name: 'Sun', sales: 20000 },
];

const stockData = [
  { name: 'Rice', usage: 45, stock: 55 },
  { name: 'Flour', usage: 30, stock: 70 },
  { name: 'Oil', usage: 25, stock: 75 },
  { name: 'Spices', usage: 40, stock: 60 },
  { name: 'Vegetables', usage: 50, stock: 50 },
];

function AdminDashboard() {
  const navigate = useNavigate();
  const { menuItems } = useMenu();

  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [todaysSales, setTodaysSales] = useState(0);
  const [salesChartData, setSalesChartData] = useState(() => {
    // Initialize last 7 days with zero sales
    return [...Array(7)].map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { name: d.toLocaleDateString(undefined, { weekday: 'short' }), sales: 0 };
    });
  });
  const [lowStockCount, setLowStockCount] = useState(0);
  const [growthRate, setGrowthRate] = useState(0);

  // Fallback static stock data (replace with inventory endpoint when available)
  const stockData = [
    { name: 'Rice', usage: 45, stock: 55 },
    { name: 'Flour', usage: 30, stock: 70 },
    { name: 'Oil', usage: 25, stock: 75 },
    { name: 'Spices', usage: 40, stock: 60 },
    { name: 'Vegetables', usage: 50, stock: 50 },
  ];

  useEffect(() => {
    let mounted = true;
    const fetchSummary = async () => {
      try {
        const orders = await ordersService.getAll();

        // Active orders (not completed or cancelled)
        const active = orders.filter(o => !['completed', 'cancelled'].includes(o.status)).length;
        if (!mounted) return;
        setActiveOrdersCount(active);

        // Today's sales
        const todayKey = new Date().toISOString().slice(0, 10);
        const todaysSum = orders.reduce((sum, o) => {
          const created = new Date(o.createdAt).toISOString().slice(0, 10);
          return created === todayKey ? sum + (parseFloat(o.total) || 0) : sum;
        }, 0);
        setTodaysSales(todaysSum);

        // Sales chart for last 7 days
        const salesByDate = {};
        orders.forEach(o => {
          const key = new Date(o.createdAt).toISOString().slice(0, 10);
          salesByDate[key] = (salesByDate[key] || 0) + (parseFloat(o.total) || 0);
        });

        const days = [...Array(7)].map((_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          const key = d.toISOString().slice(0, 10);
          return { name: d.toLocaleDateString(undefined, { weekday: 'short' }), sales: parseFloat((salesByDate[key] || 0).toFixed(2)) };
        });
        setSalesChartData(days);

        // Growth: compare last 7 days to previous 7 days
        const last7Sum = days.reduce((s, d) => s + d.sales, 0);
        const prevDays = [...Array(7)].map((_, i) => {
          const pd = new Date();
          pd.setDate(pd.getDate() - (13 - i));
          const pkey = pd.toISOString().slice(0, 10);
          return salesByDate[pkey] || 0;
        });
        const prev7Sum = prevDays.reduce((s, v) => s + v, 0);
        const growth = prev7Sum === 0 ? (last7Sum === 0 ? 0 : 100) : ((last7Sum - prev7Sum) / prev7Sum) * 100;
        setGrowthRate(parseFloat(growth.toFixed(1)));

        // Placeholder for low stock (no inventory backend yet)
        setLowStockCount(5);
      } catch (err) {
        console.error('Failed to fetch dashboard data', err);
      }
    };

    fetchSummary();
    const id = setInterval(fetchSummary, 5000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);


  return (
    <DashboardContainer>
      <Sidebar />
      <MainContent>
        <Header user={{ name: 'Admin User', role: 'Administrator' }} />
        <ContentWrapper>
          <PageHeader>
            <h1>Dashboard Overview</h1>
            <p>Welcome back! Here's what's happening at your restaurant today.</p>
          </PageHeader>

        <SummaryGrid>
          <SummaryCard onClick={() => navigate('/admin/sales')}>
            <IconWrapper color={theme.colors.primary}>
              <RupeesIcon>₹</RupeesIcon>
            </IconWrapper>
            <SummaryContent>
              <h3>Today's Sales</h3>
              <p>₹{todaysSales.toFixed(2)}</p>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard onClick={() => navigate('/admin/orders')}>
            <IconWrapper color={theme.colors.status.preparing}>
              <Receipt />
            </IconWrapper>
            <SummaryContent>
              <h3>Active Orders</h3>
              <p>{activeOrdersCount}</p>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard onClick={() => navigate('/admin/menu')}>
            <IconWrapper color={theme.colors.accentTeal}>
              <TrendingUp />
            </IconWrapper>
            <SummaryContent>
              <h3>Menu Items</h3>
              <p>{menuItems.length}</p>
            </SummaryContent>
          </SummaryCard>
        </SummaryGrid>

        <ChartsGrid>
          <ChartCard>
            <h2>Sales Overview</h2>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={salesChartData}>
                <XAxis dataKey="name" />
                <YAxis hide />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke={theme.colors.primary}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsGrid>
        </ContentWrapper>
        <Footer />
      </MainContent>
    </DashboardContainer>
  );
}

export default AdminDashboard;

